import { useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight } from 'lucide-react';
import { legalDocuments, legalLinks } from '@/content/legal';

const DocumentLink = ({ href, children, testId }) => {
  const className = 'inline-flex max-w-full items-start gap-1 rounded-sm text-cyan-400 underline underline-offset-4 transition-colors hover:text-copy-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-400';
  return href.startsWith('/')
    ? <Link data-testid={testId} className={className} to={href}>{children}</Link>
    : <a data-testid={testId} className={className} href={href}>{children}{href.startsWith('https:') && <ArrowUpRight aria-hidden="true" className="mt-1 h-3 w-3 shrink-0" />}</a>;
};

const DraftNotice = ({ kind }) => (
  <aside data-testid={`${kind}-draft-notice`} role="note" aria-label="Draft document status" className="mb-10 rounded-xl border border-amber-400/40 bg-amber-400/10 p-5 sm:p-6">
    <div className="flex items-start gap-3">
      <AlertTriangle aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
      <div>
        <p data-testid={`${kind}-draft-status`} className="font-semibold text-amber-400">Draft — under review</p>
        <p data-testid={`${kind}-draft-explanation`} className="mt-2 text-sm leading-relaxed text-copy-200">Published for transparency, not as a final policy or effective contract. Missing business details and unresolved decisions remain visible below. Publication does not add missing privacy/deletion controls or mean approval by Google or another platform.</p>
      </div>
    </div>
  </aside>
);

const LegalNavigation = ({ kind, sections }) => (
  <aside data-testid="legal-sidebar" className="min-w-0 lg:sticky lg:top-24 lg:self-start">
    <nav aria-label="Legal documents" data-testid="legal-document-navigation" className="flex flex-wrap gap-2 lg:flex-col">
      {legalLinks.map(({ id, path, label }) => <NavLink key={id} data-testid={`legal-nav-${id}`} to={path} className={({ isActive }) => `rounded-lg border px-4 py-3 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 ${isActive ? 'border-cyan-400/30 bg-cyan-400/10 font-medium text-cyan-400' : 'border-transparent text-copy-300 hover:bg-surface-900 hover:text-copy-100'}`}>{label}</NavLink>)}
    </nav>
    <nav aria-label="On this page" data-testid="legal-section-navigation" className="mt-8 hidden border-l border-surface-800 pl-4 lg:block">
      <p className="mb-3 text-xs font-medium uppercase tracking-widest text-copy-400">On this page</p>
      <ol className="space-y-2 text-sm leading-relaxed">
        {sections.map(section => <li key={section.id}><a data-testid={`legal-section-link-${section.id}`} href={`#${kind}-${section.id}`} className="rounded-sm text-copy-300 transition-colors hover:text-cyan-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400">{section.title}</a></li>)}
      </ol>
    </nav>
  </aside>
);

const LegalSection = ({ kind, section }) => {
  const key = `${kind}-${section.id}`;
  return (
    <section id={key} data-testid={`${key}-section`} aria-labelledby={`${key}-heading`} className="scroll-mt-24 border-t border-surface-800 pt-8 first:border-0 first:pt-0">
      <h2 id={`${key}-heading`} data-testid={`${key}-heading`} className="mb-5 font-display text-base font-bold text-copy-100 md:text-lg">{section.title}</h2>
      <div className="space-y-4 text-sm leading-7 text-copy-200 sm:text-base sm:leading-8">
        {section.paragraphs?.map((text, i) => <p key={`p${i}`} data-testid={`${key}-paragraph-${i}`}>{text}</p>)}
        {section.bullets && <ul className="list-disc space-y-3 pl-5 marker:text-cyan-400">{section.bullets.map((text, i) => <li key={i} data-testid={`${key}-item-${i}`} className="pl-1">{text}</li>)}</ul>}
        {section.notes?.map((text, i) => <div key={`n${i}`} data-testid={`${key}-pending-${i}`} className="rounded-r-lg border-l-2 border-amber-400/60 bg-amber-400/5 p-4 text-sm leading-relaxed"><span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-amber-400">Pending review</span>{text}</div>)}
        {section.links && <ul className="flex flex-col items-start gap-3">{section.links.map((link, i) => <li key={link.href}><DocumentLink testId={`${key}-link-${i}`} href={link.href}>{link.label}</DocumentLink></li>)}</ul>}
      </div>
    </section>
  );
};

export default function LegalPage({ kind }) {
  const doc = legalDocuments[kind];
  const { hash } = useLocation();
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${doc.title} (Draft) | ClippedUp`;
    return () => { document.title = previousTitle; };
  }, [doc.title]);
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }
    let cancelled = false;
    const scrollToSection = () => {
      if (!cancelled) document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start', behavior: 'instant' });
    };
    const frame = requestAnimationFrame(scrollToSection);
    document.fonts.ready.then(scrollToSection);
    return () => { cancelled = true; cancelAnimationFrame(frame); };
  }, [kind, hash]);
  return (
    <main data-testid={`${kind}-page`} className="relative mx-auto min-h-screen max-w-7xl px-4 pb-20 pt-28 sm:px-8 sm:pt-32">
      <header className="mb-10 max-w-3xl">
        <p data-testid="legal-page-label" className="mb-4 text-xs font-medium uppercase tracking-[0.2em] text-cyan-400">ClippedUp / Public documents</p>
        <h1 data-testid="legal-page-heading" className="font-display text-4xl font-bold tracking-tight text-copy-100 sm:text-5xl lg:text-6xl">{doc.title}</h1>
        <p data-testid="legal-page-intro" className="mt-5 text-sm leading-relaxed text-copy-300 sm:text-base">{doc.intro}</p>
        <p data-testid="legal-version" className="mt-4 text-xs text-copy-400">Public draft · Version 1 · Effective date: [Not set — pending approval]</p>
      </header>
      <DraftNotice kind={kind} />
      <div className="grid min-w-0 gap-8 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-12">
        <LegalNavigation kind={kind} sections={doc.sections} />
        <article data-testid={`${kind}-document`} aria-label={`${doc.title} draft`} className="min-w-0 space-y-10 rounded-xl border border-surface-800 bg-card p-5 [overflow-wrap:anywhere] sm:p-8 lg:p-10">
          {doc.sections.map(section => <LegalSection key={section.id} kind={kind} section={section} />)}
          <div data-testid="legal-contact" className="border-t border-surface-800 pt-8 text-sm leading-relaxed text-copy-300">
            <p className="mb-3">Questions about these drafts or your information?</p>
            <DocumentLink testId="legal-contact-email" href="mailto:clippedup.app@gmail.com">clippedup.app@gmail.com</DocumentLink>
            <p data-testid="legal-ending-notice" className="mt-5 text-xs">Draft — under review. No effective date has been set. This document is not legal advice or a certification of compliance.</p>
          </div>
        </article>
      </div>
    </main>
  );
}
